import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { AppRemixIcon, AppModal, AppNotificationAlert } from '@/layout/AppComponents';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Moment from 'moment';
import ReactDOMServer from 'react-dom/server';
import AppSpinner from '../../../../../components/AppSpinner';
import minions from '../../../../../../minions/minions';
import { axiosPath } from '../../../../../../routing/helpers/router';
import * as currentPageAction from '../../../../../../store/actions/currentPageAction';
import Translator from '../../../../../components/helpers/Translator';
import UserSelectStates from '../../../../../components/form/Selects/UserSelectStates';

const GoalStateSelect = (props) => {
  const { plantObligationID, className } = props;
  const { moduleBridgeHex, plantHex } = useParams();
  const dispatch = useDispatch();
  const permissions = useSelector(
    (s) => s.module?.[moduleBridgeHex]?.permissions
  );
  const auth = useSelector((s) => s.auth);
  const state = useSelector(
    (s) =>
      s.currentPage?.plantObligationLevels?.level1?.[plantObligationID]?.state
  );
  const goalState = useSelector(
    (s) =>
      s.currentPage?.plantObligationLevels?.level3?.[plantObligationID]
        ?.goalState
  );
  const goalCustomState = useSelector(
    (s) =>
      s.currentPage?.plantObligationLevels?.level3?.[plantObligationID]
        ?.goalCustomState
  );
  const poLevel3 = useSelector(
    (s) => s.currentPage?.plantObligationLevels?.level3
  );
  const userBridge = useSelector(
    (state) => state?.module?.[moduleBridgeHex]?.userBridge
  );
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // > CHANGE GOAL STATE
  const changeState = async (values) => {
    setOpen(false);
    setLoading(true);
    try {
      const response = await axios.post(
        axiosPath(
          "api_r_user_module_legalCompliance_evaluation_changeGoalState"
        ),
        {
          moduleBridgeHex,
          plantHex,
          plantObligation: plantObligationID,
          ...values,
        }
      );
      dispatch(
        currentPageAction.change('plantObligationLevels', {
          level3: {
            ...poLevel3,
            ...{
              [plantObligationID]: {
                ...poLevel3[plantObligationID],
                ...response.data,
              },
            },
          },
        })
      );
      setLoading(false);
      return true;
    } catch (err) {
      setLoading(false);
      AppNotificationAlert({
        title: 'Error',
        message: ReactDOMServer.renderToString(
          <Translator file="module-legalCompliance" name="state.change.error" />
        ),
        type: 'danger',
        position: 'top-center',
        duration: 0,
      });
    }
  };
  // < CHANGE GOAL STATE

  // < FORM SCHEMA
  const validationSchema = Yup.object().shape({
    state: Yup.object()
      .nullable()
      .required(ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)),
  });
  // < FORM SCHEMA

  const blockChangeState = useCallback(() => {
    if (
      permissions?.gcl?.module?.plant?.states?.goalStates?.autoChangeState4 &&
      state === 4
    ) {
      return true;
    }

    if (
      userBridge?.permissions?.level > 0 &&
      permissions?.gcl?.module?.plant?.states?.goalStates?.allowOnDateRange
        ?.dateFrom &&
      permissions?.gcl?.module?.plant?.states?.goalStates?.allowOnDateRange
        ?.dateTo
    ) {
      const currentYear = Moment().format('YYYY');
      const dateFrom = Moment(
        `${permissions?.gcl?.module?.plant?.states?.goalStates?.allowOnDateRange?.dateFrom}/${currentYear}`,
        'DD/MM/YYYY'
      );
      const dateTo = Moment(
        `${permissions?.gcl?.module?.plant?.states?.goalStates?.allowOnDateRange?.dateTo}/${currentYear}`,
        'DD/MM/YYYY'
      );

      if (!Moment().isBetween(dateFrom, dateTo, undefined, '[]')) {
        return true;
      }
    }

    return false;
  }, [permissions, state, userBridge]);

  return (
    <>
      <div
        className={`relative ${
          blockChangeState() ? 'not-allowed' : 'pointer'
        } ${className || ''}`}
        style={{
          backgroundColor: minions.obligation.stateColor(
            goalState || 0,
            goalCustomState && auth.customStates[goalCustomState]
          ),
        }}
        onClick={() => (blockChangeState() ? undefined : setOpen(true))}
      >
        {!loading ? (
          minions.obligation.stateIcon(
            goalState || 0,
            goalCustomState && auth.customStates[goalCustomState]
          )
        ) : (
          <AppSpinner>
            <AppRemixIcon icon="loader-4" size={30} color="white" />
          </AppSpinner>
        )}
        <StateSelectExpand />
      </div>
      <AppModal show={!!open} onClose={() => setOpen(false)}>
        <Formik
          validateOnChange={false}
          validateOnBlur={false}
          enableReinitialize
          validationSchema={validationSchema}
          initialValues={{
            state: null,
          }}
          onSubmit={changeState}
        >
          {({ setFieldValue, handleSubmit, values, isSubmitting }) => (
            <>
              <UserSelectStates
                onHandleChange={(opt) => setFieldValue('state', opt)}
                value={values?.state}
                isMulti={false}
              />
              <span
                className="btn btn-success float-right mt-3"
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                <Translator file="general" name="general.update" />
              </span>
            </>
          )}
        </Formik>
      </AppModal>
    </>
  );
};

export default GoalStateSelect;

const StateSelectExpand = styled.div`
  width: 0;
  overflow: hidden;
  position: absolute;
  top: 3px;
  left: 42px;
  height: 154px;
  z-index: 10;
  transition: width ease 0.4s;
  font-size: 14px;
  background: white;
`;
